import React, { useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import Sidebar from './Sidebar';
import MobileHeader from './MobileHeader';
import UserMenu from './UserMenu';
import ChatWidget from '../chat/ChatWidget';
import { useTheme } from '../../context/ThemeContext';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  user?: {
    name: string;
    email: string;
    role: string;
  };
}

export const Layout: React.FC<LayoutProps> = ({ children, title, user }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        user={user}
      />

      {/* Mobile Header - Visible solo en < lg (mobile y tablet) */}
      <MobileHeader
        onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)}
        title={title}
      />

      {/* Main content - pt-16 en mobile/tablet para el header fijo, pl-64 en desktop para sidebar */}
      <div className="transition-all duration-300 pt-16 lg:pt-0 lg:pl-64">
        {/* Desktop Header - Solo visible en lg+ */}
        <header className="hidden lg:block sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-4">
              {/* Page title */}
              {title && (
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  {title}
                </h1>
              )}
            </div>

            {/* Header actions - Issue #25: Agregar UserMenu */}
            <div className="flex items-center space-x-4">
              {/* Theme Toggle Button */}
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Cambiar tema"
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                )}
              </button>

              {/* User Menu */}
              {user && (
                <UserMenu
                  userName={user.name}
                  userEmail={user.email}
                  userRole={user.role}
                />
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* Chat Widget - Solo visible si el usuario está logueado */}
      {user && <ChatWidget />}
    </div>
  );
};

export default Layout;
